// Shared data shapes. Signed things travel as the exact bytes that were signed
// (base64url of canonical JSON), so no app ever has to rebuild JSON byte-for-byte:
// check the signature over the bytes first, then read them.

export const LEVELS = ['alert', 'shelter', 'evacuate', 'clear', 'false_alarm'] as const;
export type Level = (typeof LEVELS)[number];

/** Levels that play the loud alarm (Critical Alert on iPhone, alarm channel on Android). */
export const URGENT_LEVELS: readonly Level[] = ['shelter', 'evacuate'];

/** iPhone apps can watch at most 20 areas, so a site has at most 20 zones. */
export const MAX_ZONES = 20;
/** A language code: ISO 639 (two or three letters), e.g. en, ar, pt, sw, fil. */
export const LANGUAGE_CODE = /^[a-z]{2,3}$/;
/** The longest a single word or phrase may be. */
export const MAX_PHRASE = 1000;

export interface Zone {
  id: string; // "01".."20"
  name: string;
  /** [longitude, latitude] points, WGS84, not closed (first != last). */
  polygon: [number, number][];
}

export interface Signer {
  keyId: string;
  /** base64url raw uncompressed P-256 public key (65 bytes). */
  publicKey: string;
}

/** Our provider key. The keys that approve a site's changes are its ops tablets' own keys. */
export interface Signers {
  provider: Signer;
}

export interface TabletEntry extends Signer {
  label: string;
  addedAt: string;
}

export interface SiteBundle {
  v: 1;
  siteId: string;
  version: number;
  name: string;
  zones: Zone[];
  /** Map area shown on the tablet and phones: [west, south, east, north], and the picture under the zones. */
  map: { bbox: [number, number, number, number]; picture?: MapPictureRef };
  tablets: TabletEntry[];
  /** messages[language][level] -> the alert instruction staff read (any language: en, ar, pt, ...). */
  messages: Record<string, Record<Level, string>>;
  /**
   * phrases[language][key] -> every other word staff see in that language (phrases.ts).
   * English and Arabic are built in, so a site only needs these for other languages (or
   * to change ours). A language is only offered when its set is complete.
   */
  phrases?: Record<string, Record<string, string>>;
  signers: Signers;
  issuedAt: string;
}

/**
 * The map picture, covering exactly the map area. The bundle carries only its fingerprint:
 * tablets and phones download the picture from our server, keep it, and use it only if its
 * SHA-256 matches, so a picture can't be swapped without the ops tablet approving it.
 */
export interface MapPictureRef {
  sha256: string;
  width: number;
  height: number;
  /** Shown on the map, e.g. "Contains modified Copernicus Sentinel data 2026". */
  credit: string;
}

/** The client's approval, made on the site's ops tablet with the tablet's own hardware key. */
export interface Approval {
  keyId: string; // the approving tablet
  sig: string; // base64url raw r||s over PAE(APPROVAL_TYPE, payload bytes)
}

export interface SignedBundle {
  payload: string; // base64url of the bundle's canonical JSON: the exact signed bytes
  providerSig: string; // base64url raw r||s over PAE(BUNDLE_TYPE, payload bytes)
  approval: Approval;
}

export interface AlertBody {
  v: 1;
  alertId: string;
  siteId: string;
  bundleVersion: number;
  zones: string[];
  level: Level;
  issuedAt: string; // UTC, e.g. 2026-09-26T08:14:00.000Z
  tabletKeyId: string;
  test: boolean;
}

export interface SignedAlert {
  payload: string; // base64url of the alert's canonical JSON
  sig: string; // base64url raw r||s over PAE(ALERT_TYPE, payload bytes), from the tablet's hardware key
}

/** What the site's QR code carries. It holds no secrets. */
export interface JoinCode {
  siteId: string;
  /** signersFingerprint() of the site's signers when the QR code was printed. */
  signers: string;
}

/** `reason` is a stable code that every app must match; `detail` is for people. */
export type Failure = { ok: false; reason: string; detail?: string };
export type Result = { ok: true } | Failure;

export const fail = (reason: string, detail?: string): Failure =>
  detail === undefined ? { ok: false, reason } : { ok: false, reason, detail };

const TIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{3})?Z$/;

const isStr = (x: unknown): x is string => typeof x === 'string' && x.length > 0;
export const isTime = (x: unknown): x is string => typeof x === 'string' && TIME.test(x) && !Number.isNaN(Date.parse(x));
const isObj = (x: unknown): x is Record<string, unknown> => typeof x === 'object' && x !== null && !Array.isArray(x);
const isSigner = (s: unknown): boolean => isObj(s) && isStr(s.keyId) && isStr(s.publicKey);
const isLonLat = (p: unknown): boolean =>
  Array.isArray(p) && p.length === 2 && Number.isFinite(p[0]) && Number.isFinite(p[1]) && Math.abs(p[0]) <= 180 && Math.abs(p[1]) <= 90;

export function checkAlertShape(a: unknown): Result {
  if (!isObj(a)) return fail('bad_alert', 'not an object');
  const x = a as Partial<AlertBody>;
  if (x.v !== 1) return fail('bad_alert', 'unsupported version');
  if (!isStr(x.alertId) || !isStr(x.siteId) || !isStr(x.tabletKeyId)) return fail('bad_alert', 'ids missing');
  if (!Number.isInteger(x.bundleVersion) || (x.bundleVersion as number) < 1) return fail('bad_alert', 'bundleVersion invalid');
  const zones = x.zones;
  if (!Array.isArray(zones) || zones.length === 0 || zones.length > MAX_ZONES || !zones.every(isStr) || new Set(zones).size !== zones.length) {
    return fail('bad_alert', 'zones invalid');
  }
  if (!LEVELS.includes(x.level as Level)) return fail('bad_alert', 'unknown level');
  if (!isTime(x.issuedAt)) return fail('bad_alert', 'issuedAt invalid');
  if (typeof x.test !== 'boolean') return fail('bad_alert', 'test flag missing');
  return { ok: true };
}

export function checkBundleShape(b: unknown): Result {
  if (!isObj(b)) return fail('bad_bundle', 'not an object');
  const x = b as Partial<SiteBundle>;
  if (x.v !== 1) return fail('bad_bundle', 'unsupported version');
  if (!isStr(x.siteId) || !isStr(x.name)) return fail('bad_bundle', 'ids missing');
  if (!Number.isInteger(x.version) || (x.version as number) < 1) return fail('bad_bundle', 'version invalid');
  if (!Array.isArray(x.zones) || x.zones.length === 0 || x.zones.length > MAX_ZONES) return fail('bad_bundle', `zones must number 1-${MAX_ZONES}`);
  const ids = new Set<string>();
  for (const z of x.zones) {
    if (!isObj(z) || !isStr(z.id) || !isStr(z.name) || ids.has(z.id)) return fail('bad_bundle', 'zone id/name invalid or duplicated');
    ids.add(z.id);
    if (!Array.isArray(z.polygon) || z.polygon.length < 3) return fail('bad_bundle', `zone ${z.id} needs 3+ points`);
    if (!z.polygon.every(isLonLat)) return fail('bad_bundle', `zone ${z.id} has an invalid point`);
  }
  const bb = x.map?.bbox;
  if (!Array.isArray(bb) || bb.length !== 4 || !bb.every((n) => Number.isFinite(n)) || bb[0] >= bb[2] || bb[1] >= bb[3]) {
    return fail('bad_bundle', 'map bbox invalid');
  }
  const pic = x.map?.picture;
  if (pic !== undefined) {
    const okSize = (n: unknown) => Number.isInteger(n) && (n as number) >= 1 && (n as number) <= 8192;
    if (!isObj(pic) || typeof pic.sha256 !== 'string' || !/^[0-9a-f]{64}$/.test(pic.sha256) || !okSize(pic.width) || !okSize(pic.height) || !isStr(pic.credit) || pic.credit.length > 200) {
      return fail('bad_bundle', 'map picture invalid');
    }
  }
  // The tablets approve every change, so a site always has at least one.
  if (!Array.isArray(x.tablets) || x.tablets.length === 0 || !x.tablets.every((t) => isSigner(t) && isStr(t.label) && isTime(t.addedAt))) {
    return fail('bad_bundle', 'tablets invalid');
  }
  if (new Set(x.tablets.map((t) => t.keyId)).size !== x.tablets.length) return fail('bad_bundle', 'tablet listed twice');
  if (!isObj(x.messages) || !x.messages.en) return fail('bad_bundle', 'messages (with en) missing');
  for (const [lang, m] of Object.entries(x.messages)) {
    if (!LANGUAGE_CODE.test(lang)) return fail('bad_bundle', `language code ${lang} invalid`);
    if (!isObj(m) || !LEVELS.every((l) => isStr(m[l]))) return fail('bad_bundle', `messages for ${lang} incomplete`);
  }
  if (x.phrases !== undefined) {
    if (!isObj(x.phrases)) return fail('bad_bundle', 'phrases invalid');
    for (const [lang, p] of Object.entries(x.phrases)) {
      if (!(lang in x.messages)) return fail('bad_bundle', `phrases for ${lang}, which has no messages`);
      if (!isObj(p) || !Object.values(p).every((v) => typeof v === 'string' && v.length <= MAX_PHRASE)) return fail('bad_bundle', `phrases for ${lang} invalid`);
    }
  }
  const sg = x.signers;
  if (!isObj(sg) || !isSigner(sg.provider)) return fail('bad_bundle', 'signers invalid');
  if (!isTime(x.issuedAt)) return fail('bad_bundle', 'issuedAt invalid');
  return { ok: true };
}
